import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import styled from "styled-components";
import InputBox from "../Generic/InputBox";
import Checkbox from "../Generic/Checkbox";
import Dropdown from "../Generic/Dropdown";
import { deepCopy, isEmpty } from "../util/util";
import Button from "../Generic/Button";
import { errorNotification, successNotification } from "../Generic/Notifications";
import LandingConnector from "../Landing/Connector/LandingConnector";
import InventoryPlanService from "./Services/InventoryPlanService";
import { useNavigate, useSearchParams } from "react-router-dom";
import { customDataTableStyles } from "./Styles/DataTableStyles";
import { ColumnHeader, WrappedText } from "./Styles/DataTableCustomComponents";
import { parseMetaData } from "./Services/InventoryPlanErrorHandlers";
import SearchableDropdown from "../Generic/SearchableDropdown";
import { DEFAULT_QUANTITY_UNIT } from "../config/app.config";

const MODULE_PREFIX = "m";
const SYSTEM_PREFIX = "s";

const Page = styled.div`
	height: max-content;
	width: 100%;
	display: flex;
	flex-direction: column;
	padding: 2rem;
	gap: 2rem;
	overflow-x: hidden;
	overflow-y: auto;
`;

const Header = styled.div`
	display: flex;
	font-size: 2rem;
	font-weight: 600;
`;

const Block = styled.div`
	width: 100%;
	height: auto;
	display: flex;
	gap: ${(props) => props.gap || "1rem"};
	align-items: center;
`;

const Div = styled.div`
	background: ${(props) => props.background};
	color: ${(props) => props.color};
	position: ${(props) => props.position};
	width: ${(props) => props.width};
	display: flex;
	flex-direction: ${(props) => props.flexDirection};
	justify-content: ${(props) => props.jContent};
	align-items: ${(props) => props.alignItem};
	padding: ${(props) => props.padding};
	margin: ${(props) => props.margin};
	text-align: ${(props) => props.txtAlign};
	font-size: ${(props) => props.fontSize};
	gap: ${(props) => props.gap};
	overflow-y: ${(props) => props.overflowY};
	overflow-x: ${(props) => props.overflowX};
	height: ${(props) => props.height};
	max-height: ${(props) => props.maxHeight};
	min-height: ${(props) => props.minHeight};
	max-width: ${(props) => props.maxWidth};
	white-space: ${(props) => props.whiteSpace};
	border: ${(props) => props.border};
	border-radius: ${(props) => props.borderRadius};
	flex-wrap: ${(props) => props.flexWrap};
	@media (max-width: 1000px) {
		justify-content: ${(props) => props.jContentBefore1000};
	}
`;

//TODO: MOVE ALL THIS TO A GLOBAL CONFIG.

const DEFAULT_BASE_ITEM = "circular_white_plate"; //TODO: FInd a better way to do this later.

const MODULE_TYPES = {
	DISPENSER: 1,
	PLATE_RACKS: 2,
};

const DISPENSER_TYPES = {
	CONVEYOR: 20,
	PUMP: 30,
};

const Actions = {
	UNDOCK: "undock",
	DOCK: "dock",
	NO_CHANGE: "noChange",
	REPLACE: "replace",
};

const ActionOptions = [
	{ head: "Undock", value: Actions.UNDOCK },
	{ head: "Dock", value: Actions.DOCK },
	{ head: "No Change", value: Actions.NO_CHANGE },
];

const AdditionalActions = [{ head: "Replace", value: Actions.REPLACE }];

const TABS = {
	CONVEYOR_DISPENSERS: "CONVEYOR_DISPENSERS",
	PUMP_DISPENSERS: "PUMP_DISPENSERS",
	EPRS: "EPRS",
};

function shouldCellBeDisabled(data) {
	if (data?.action === Actions.NO_CHANGE || data?.action === Actions.UNDOCK) return true;
	return false;
}

function generateTableColumns(tab, { getFilteredOptions, updateTableData, inventoryFetched, erroredItems, removeErroredItem }) {
	switch (tab) {
		case TABS.CONVEYOR_DISPENSERS:
			return [
				{
					name: "Symbol",
					cell: (data) => <img height={"80px"} src={data.moduleSymbol} alt="module-symbol" />,
					grow: 1,
					center: true,
				},
				{
					name: "Mapping",
					cell: (data) => <img height={"80px"} src={data.mappingImage} alt="dispenser-mapping" />,
					grow: 1,
					center: true,
				},
				{
					name: <ColumnHeader>Current Ingredient Name</ColumnHeader>,
					omit: inventoryFetched === false,
					cell: (data) => <WrappedText>{data.currentInventoryData?.name}</WrappedText>,
					wrap: false,
					center: true,
				},
				{
					name: <ColumnHeader>Current Quantity</ColumnHeader>,
					omit: inventoryFetched === false,
					cell: (data) => <span>
						{data.currentInventoryData?.availableAmount ?? 0} {DEFAULT_QUANTITY_UNIT}
					</span>,
					center: true,
				},
				{
					name: <ColumnHeader>Current Status</ColumnHeader>,
					omit: inventoryFetched === false,
					selector: (data) => (data.currentInventoryData?.isDocked ? "Dock" : "Undock"),
					center: true,
				},
				{
					name: <ColumnHeader>Action</ColumnHeader>,
					cell: (data) => {
						let actions = inventoryFetched ? [...ActionOptions, ...AdditionalActions] : ActionOptions;
						return (
							<Dropdown
								margin="0"
								minWidth="6rem"
								optionsList={actions}
								value={data?.action}
								onChange={(val) => updateTableData(tab, data.moduleId, data.systemId, "action", val)}
							/>
						);
					},
					grow: 2,
					allowOverflow: false,
					center: true,
				},
				{
					name: <ColumnHeader>Ingredient</ColumnHeader>,
					cell: (data) => {
						const options = getFilteredOptions(data.moduleType, data.systemType);
						const disabled = shouldCellBeDisabled(data);
						const defaultSelected = { value: data?.itemId, head: data?.itemDetails?.name };
						return (
							<SearchableDropdown
								disabled={disabled}
								list={options}
								defaultSelected={defaultSelected}
								onChange={({ value }) => updateTableData(tab, data.moduleId, data.systemId, "itemId", value)}
							/>
						);
					},
					grow: 2,
					center: true,
				},
				{
					name: <ColumnHeader>Max Fill Allowed</ColumnHeader>,
					cell: (data) => <span>
						{data?.itemDetails?.inventoryData?.fillDetails?.maxAmount ?? 0} {DEFAULT_QUANTITY_UNIT}
					</span>,
					center: true,
				},
				{
					name: <ColumnHeader>Amount to be filled</ColumnHeader>,
					cell: (data) => {
						const disabled = shouldCellBeDisabled(data);
						const key = `${data.moduleId}-${data.systemId}`;
						let hasErrored = false,
							errorMessage = "";
						if (erroredItems?.[key]) {
							hasErrored = true;
							errorMessage = erroredItems[key];
						}

						return (
							<>
								<InputBox
									errored={hasErrored}
									errorMessage={errorMessage}
									disabled={disabled}
									width="100%"
									type="number"
									defaultValue={data?.amountToBeFilled || 0}
									onChange={(e) => {
										if (hasErrored) {
											removeErroredItem(key);
										}
										updateTableData(tab, data.moduleId, data.systemId, "amountToBeFilled", e.target.valueAsNumber);
									}}
								/>
								<div style={{ fontSize:"16px", marginLeft:"0.5rem" }} >{DEFAULT_QUANTITY_UNIT}</div>
							</>
						);
					},
					center: true,
				},
				{
					name: <ColumnHeader>Container Type</ColumnHeader>,
					selector: (data) => data?.itemDetails?.inventoryData?.dispenseDetails?.dispenserConfig?.containerType,
					center: true,
				},
				{
					name: <ColumnHeader>Base Type</ColumnHeader>,
					selector: (data) => data?.itemDetails?.inventoryData?.dispenseDetails?.dispenserConfig?.baseType,
					center: true,
				},
				{
					name: <ColumnHeader>Spout Type</ColumnHeader>,
					selector: (data) => data?.itemDetails?.inventoryData?.dispenseDetails?.dispenserConfig?.spoutType,
					center: true,
				},
			];

		case TABS.PUMP_DISPENSERS:
			return [
				{
					name: "Symbol",
					cell: (data) => <img height={"80px"} src={data.moduleSymbol} alt="module-symbol" />,
					grow: 1,
					center: true,
				},
				{
					name: "Mapping",
					cell: (data) => <img height={"80px"} src={data.mappingImage} alt="dispenser-mapping" />,
					grow: 1,
					center: true,
				},
				{
					name: <ColumnHeader>Current Ingredient Name</ColumnHeader>,
					omit: inventoryFetched === false,
					cell: (data) => <WrappedText>{data.currentInventoryData?.name}</WrappedText>,
					wrap: false,
					allowOverflow: false,
					center: true,
				},
				{
					name: <ColumnHeader>Current Quantity</ColumnHeader>,
					right: false,
					omit: inventoryFetched === false,
					cell: (data) => (
						<span>
							{data.currentInventoryData?.availableAmount || 0} {DEFAULT_QUANTITY_UNIT}
						</span>
					),
					center: true,
				},
				{
					name: <ColumnHeader>Current Status</ColumnHeader>,
					omit: inventoryFetched === false,
					selector: (data) => (data.currentInventoryData?.isDocked ? "Dock" : "Undock"),
					center: true,
				},
				{
					name: <ColumnHeader>Action</ColumnHeader>,
					cell: (data) => {
						let actions = inventoryFetched ? [...ActionOptions, ...AdditionalActions] : ActionOptions;
						return (
							<Dropdown
								margin="0"
								optionsList={actions}
								value={data?.action}
								onChange={(val) => updateTableData(tab, data.moduleId, data.systemId, "action", val)}
							/>
						);
					},
					grow: 1,
					center: true,
				},
				{
					name: <ColumnHeader>Ingredient</ColumnHeader>,
					cell: (data) => {
						const options = getFilteredOptions(data.moduleType, data.systemType);
						const disabled = shouldCellBeDisabled(data);
						const defaultSelected = { value: data?.itemId, head: data?.itemDetails?.name };
						return (
							<SearchableDropdown
								disabled={disabled}
								list={options}
								defaultSelected={defaultSelected}
								onChange={({ value }) => updateTableData(tab, data.moduleId, data.systemId, "itemId", value)}
							/>
						);
					},
					grow: 2,
					center: true,
				},
				{
					name: <ColumnHeader>Max Fill Allowed</ColumnHeader>,
					cell: (data) => (
						<span>
							{data?.itemDetails?.inventoryData?.fillDetails?.maxAmount || 0} {DEFAULT_QUANTITY_UNIT}
						</span>
					),
					center: true,
				},
				{
					name: <ColumnHeader>Amount to be filled</ColumnHeader>,
					cell: (data) => {
						const disabled = shouldCellBeDisabled(data);
						const key = `${data.moduleId}-${data.systemId}`;
						let hasErrored = false,
							errorMessage = "";
						if (erroredItems?.[key]) {
							hasErrored = true;
							errorMessage = erroredItems[key];
						}
						return (
							<>
								<InputBox
									errored={hasErrored}
									errorMessage={errorMessage}
									disabled={disabled}
									type="number"
									width="100%"
									defaultValue={data?.amountToBeFilled}
									onChange={(e) => {
										if (hasErrored) {
											removeErroredItem(key);
										}
										updateTableData(tab, data.moduleId, data.systemId, "amountToBeFilled", e.target.valueAsNumber);
									}}
								/>
								<div style={{ fontSize:"16px", marginLeft:"0.5rem" }} >{DEFAULT_QUANTITY_UNIT}</div>
							</>
						);
					},
					center: true,
				},
				{
					name: <ColumnHeader>Co dispense</ColumnHeader>,
					omit: true,
					cell: (data) => {
						const disabled = shouldCellBeDisabled(data);
						return (
							<Checkbox
								disabled={disabled}
								value={data?.coDispense}
								onClick={(e) => updateTableData(tab, data.moduleId, data.systemId, "coDispense", e.target.checked)}
							/>
						);
					},
					center: true,
				},
				{
					name: <ColumnHeader>Agitation Required</ColumnHeader>,
					cell: (data) => {
						if (!data.itemId) return;

						const defaultValue = data?.itemDetails?.inventoryData?.dispenseDetails?.dispenserConfig?.agitator;
						if (typeof defaultValue !== "undefined") {
							if (typeof defaultValue === "object") {
								return JSON.stringify(defaultValue);
							} else {
								return defaultValue === true ? "Yes" : "No";
							}
						}
					},
					center: true,
				},
			];

		case TABS.EPRS:
			return [
				{
					name: "Symbol",
					cell: (data) => <img height={"80px"} src={data.moduleSymbol} alt="module-symbol" />,
					center: true,
				},
				{
					name: <ColumnHeader>Current Quantity</ColumnHeader>,
					omit: inventoryFetched === false,
					selector: (data) => data?.currentAvailableAmount,
					center: true,
				},
				{
					name: <ColumnHeader>Total Number of Bowls</ColumnHeader>,
					selector: (data) => data?.availableAmount,
					center: true,
				},
				{
					name: <ColumnHeader>Action</ColumnHeader>,
					cell: (data) => {
						let actions = inventoryFetched ? [...ActionOptions, ...AdditionalActions] : ActionOptions;
						return (
							<Dropdown
								margin="0"
								optionsList={actions}
								value={data?.action}
								onChange={(val) => updateTableData(tab, data.moduleId, null, "action", val)}
							/>
						);
					},
					grow: 1,
					center: true,
				},
			];

		default:
			return [];
	}
}

const CreateInventoryPlan = (props) => {
	const { machineID } = props;
	const [searchParams, setSearchParams] = useSearchParams();
	const [activeTab, setActiveTab] = useState(TABS.CONVEYOR_DISPENSERS);
	const [loading, setLoading] = useState(true);
	const [lookupData, setLookupData] = useState({});
	const [tableData, setTableData] = useState({});
	const [planName, setPlanName] = useState("");
	const [inventoryFetched, setInventoryFetched] = useState(false);
	const [erroredItems, setErroredItems] = useState({});
	const [saveButtonDisabled, disableSaveButton] = useState(false);
	const navigate = useNavigate();

	function removeErroredItem(key) {
		setErroredItems(({ [key]: _, ...rest }) => rest);
	}

	function renderActiveTab(activeTab, helperFunctions, tableData, loading) {
		const columns = generateTableColumns(activeTab, helperFunctions);
		const tabData = tableData?.[activeTab]?.groups || tableData?.[activeTab]?.records;
		let renderData = [];

		renderData.push(
			<DataTable
				progressPending={loading}
				customStyles={customDataTableStyles}
				key={activeTab}
				responsive={true}
				striped
				fixedHeader
				columns={columns}
				data={tabData}
			/>
		);

		return renderData;
	}

	async function fetchInventoryDetails(fillWithCurrentInventory = false) {
		setLoading(true);
		const payload = {
			restaurantId: machineID,
			brandId: "kocafe",
		};
		const { data, error } = await InventoryPlanService.getInventoryDetails(payload);
		if (data) {
			const dataTablePayload = prepareDataTablePayload(data);
			setLookupData(data);
			if (fillWithCurrentInventory) {
				//TODO: Make this a parallel call for better performance
				fillTableWithCurrentInventory(dataTablePayload, data);
			} else {
				setTableData(dataTablePayload);
				setLoading(false);
			}
		}
		if (error) {
			errorNotification(error);
			setLoading(false);
		}
	}

	useEffect(() => {
		let queryParamAction = searchParams.get("fill_with_current_inventory");
		const fillWithCurrentInventory = queryParamAction === "true" ? true : false;
		fetchInventoryDetails(fillWithCurrentInventory);
	}, []);

	useEffect(() => {
		if (loading || !isEmpty(erroredItems)) {
			disableSaveButton(true);
		} else {
			disableSaveButton(false);
		}
	}, [loading, erroredItems]);

	function getFilteredOptions(moduleType, dispenserType = DISPENSER_TYPES.CONVEYOR) {
		let fileredOptions = [];

		switch (moduleType) {
			case MODULE_TYPES.DISPENSER:
				for (const ingredientId in lookupData.ingredientsData) {
					const { isBaseItem, inventoryData } = lookupData.ingredientsData[ingredientId];
					if (!isBaseItem && inventoryData?.dispenseDetails?.dispenserType === dispenserType) {
						fileredOptions.push({ ...lookupData.ingredientsData[ingredientId] });
					}
				}
				break;

			case MODULE_TYPES.PLATE_RACKS:
				for (const ingredientId in lookupData.ingredientsData) {
					const { isBaseItem } = lookupData.ingredientsData[ingredientId];
					if (isBaseItem) {
						fileredOptions.push({ ...lookupData.ingredientsData[ingredientId] });
					}
				}
				break;

			default:
				break;
		}

		fileredOptions = fileredOptions.map(({ _id, name }) => {
			return { value: _id, head: name };
		});

		return fileredOptions;
	}

	function getAvailableAmount(action, initialAmount) {
		switch (action) {
			case Actions.DOCK:
			case Actions.REPLACE:
				return 1;
			case Actions.UNDOCK:
				return 0;
			default:
				return initialAmount;
		}
	}

	function updateTableData(tab, moduleId, systemId, key, value) {
		if (!tableData?.[tab]) return;

		let newTableData = deepCopy(tableData[tab]);

		switch (tab) {
			case TABS.CONVEYOR_DISPENSERS:
			case TABS.PUMP_DISPENSERS:
				var tableRecordIndex = newTableData.records.findIndex(
					(record) => record.moduleId === moduleId && record.systemId === systemId
				);
				newTableData.records[tableRecordIndex][key] = value;

				if (key === "itemId") {
					let itemDetails = lookupData.ingredientsData.find(({ _id }) => _id === value);
					newTableData.records[tableRecordIndex]["itemDetails"] = itemDetails;
				}

				break;

			case TABS.EPRS:
				let { groups, records } = newTableData;
				const eprGroupIndex = groups.findIndex((item) => item.moduleId === moduleId);
				groups[eprGroupIndex][key] = value;

				if (key === "itemId") {
					let itemDetails = lookupData.ingredientsData.find(({ _id }) => _id === value);
					for (const recordId in records) {
						if (records[recordId].moduleId === moduleId) {
							records[recordId]["itemDetails"] = itemDetails;
						}
					}
				}

				if (key === "action") {
					for (const recordId in records) {
						if (records[recordId].moduleId === moduleId) {
							records[recordId]["action"] = value;
							records[recordId]["amountToBeFilled"] = getAvailableAmount(value, records[recordId]["availableAmount"]);
						}
					}
				}

				break;

			default:
				break;
		}

		setTableData((prev) => ({ ...prev, [tab]: newTableData }));
	}

	function prepareDataTablePayload(data) {
		let tableData = {},
			conveyorDispensers = [],
			pumpDispensers = [],
			eprs = [],
			eprGroups = [];
		const { inventoryData, ingredientsData } = data;
		const defaultBaseItemDetails = ingredientsData.find(({ _id }) => _id === DEFAULT_BASE_ITEM);
		for (const moduleId in inventoryData) {
			const moduleData = inventoryData[moduleId];
			for (const systemId in moduleData.systems) {
				let recordData = {
					moduleSymbol: moduleData.moduleSymbol,
					mappingImage: moduleData.systems[systemId].mappingImage,
					moduleId: moduleId,
					systemId: systemId,
					systemType: moduleData.systems[systemId].systemType,
					moduleType: moduleData.moduleType,
					amountToBeFilled: 0,
					action: Actions.NO_CHANGE,
					layer: moduleData.layer,
				};

				switch (moduleData.moduleType) {
					case MODULE_TYPES.DISPENSER:
						const dispenserType = moduleData.systems[systemId]?.systemType;
						if (dispenserType === DISPENSER_TYPES.CONVEYOR) {
							conveyorDispensers.push(recordData);
						} else if (dispenserType === DISPENSER_TYPES.PUMP) {
							pumpDispensers.push(recordData);
						}
						break;

					case MODULE_TYPES.PLATE_RACKS:
						const eprGroupIndex = eprGroups.findIndex((item) => item.moduleId === moduleId);
						recordData = {
							...recordData,
							itemId: DEFAULT_BASE_ITEM,
							itemDetails: defaultBaseItemDetails,
							amountToBeFilled: 1,
							action: Actions.DOCK,
						};

						eprs.push(recordData);

						if (eprGroups[eprGroupIndex]) {
							eprGroups[eprGroupIndex].availableAmount++;
						} else {
							eprGroups.push({
								moduleSymbol: moduleData.moduleSymbol,
								availableAmount: 1,
								action: Actions.DOCK,
								itemId: DEFAULT_BASE_ITEM,
								itemDetails: defaultBaseItemDetails,
								moduleId,
							});
						}

						break;

					default:
						break;
				}
			}
		}
		tableData = {
			[TABS.CONVEYOR_DISPENSERS]: { records: conveyorDispensers },
			[TABS.PUMP_DISPENSERS]: { records: pumpDispensers },
			[TABS.EPRS]: { records: eprs, groups: eprGroups },
		};

		return tableData;
	}

	function generateCreatePlanPayload(tableData) {
		let planData = {},
			payload = {};

		if (!planName) {
			errorNotification("Please provide a plan name");
			return;
		}

		function extractDataFromTableRecords(recordData) {
			let modules = {};
			for (const recordIndex in recordData) {
				let {
					moduleId,
					systemId,
					itemId,
					coDispense,
					action,
					amountToBeFilled,
					moduleSymbol,
					layer,
					itemDetails,
					moduleType,
					systemType,
					mappingImage,
				} = recordData[recordIndex];

				if (action === Actions.NO_CHANGE) continue;

				if (action === Actions.REPLACE) {
					action = Actions.DOCK;
				}

				const moduleKey = String(moduleId);
				const systemKey = String(systemId);

				const systemDetails = {
					itemId,
					name: itemDetails?.name,
					itemDetails,
					coDispense: coDispense ?? false,
					amountToBeFilled: amountToBeFilled || 0,
					action,
					systemType,
					mappingImage,
				};

				// Update or create the module and system entries
				if (modules[moduleKey]) {
					modules[moduleKey]["systems"][systemKey] = systemDetails;
				} else {
					modules[moduleKey] = {
						moduleShape: "",
						moduleSymbol,
						layer,
						moduleType,
						systems: {
							[systemKey]: systemDetails,
						},
					};
				}
			}

			return modules;
		}

		for (const tab in tableData) {
			let modulesData = {};
			modulesData = { ...modulesData, ...extractDataFromTableRecords(tableData[tab].records) };
			planData = { ...planData, ...modulesData };
		}

		if (isEmpty(planData)) {
			errorNotification("Plan Data Can't be empty");
			return;
		}

		payload = {
			name: planName,
			plan: { modules: planData },
			restaurantId: machineID,
		};

		return payload;
	}

	async function saveInventoryPlan() {
		const payload = generateCreatePlanPayload(tableData);
		if (!payload) return;
		const { data, error, metadata } = await InventoryPlanService.createInventoryPlan(payload);
		if (data) {
			successNotification("Success");
			const { _id } = data;
			navigate(`/${machineID}/inventoryPlan/details/${_id}`);
		}
		if (error) {
			errorNotification(error);
			if (metadata) {
				let erroredItems = parseMetaData(metadata);
				setErroredItems(erroredItems);
			}
		}
	}

	async function getCurrentInventory() {
		return await InventoryPlanService.getCurrentInventory(machineID);
	}

	function updateTableDataWithInventory(tableData, inventoryData, lookupData) {
		let newTableData = deepCopy(tableData);

		function replaceTabDataWithInventoryData(tabData) {
			let newTabData = deepCopy(tabData);

			for (const recordIndex in tabData) {
				const { moduleId, systemId } = tabData[recordIndex];
				let newRecordData = { action: Actions.NO_CHANGE };
				const currentInventoryModule = inventoryData?.modules?.[moduleId];
				if (currentInventoryModule) {
					const currentInventorySystem = currentInventoryModule?.systems?.[systemId];
					const { itemId, availableAmount, isDocked } = currentInventorySystem;
					const itemDetails = lookupData.ingredientsData.find(({ _id }) => _id === itemId);
					if (itemDetails) {
						newRecordData = {
							...newRecordData,
							itemId,
							currentInventoryData: { itemId, name: itemDetails.name, isDocked, availableAmount },
							itemDetails,
						};
					}
				}

				newTabData[recordIndex] = {
					...tabData[recordIndex],
					...newRecordData,
				};
			}
			return newTabData;
		}

		function updateGroupData(groupData) {
			let newGroupData = deepCopy(groupData);
			for (const recordIndex in groupData) {
				const { moduleId } = groupData[recordIndex];
				let newRecordData = { action: Actions.NO_CHANGE };
				const currentInventoryModule = inventoryData?.modules?.[moduleId];
				let currentAvailableAmount = 0;

				if (currentInventoryModule) {
					for (const system in currentInventoryModule?.systems) {
						const { availableAmount } = currentInventoryModule?.systems[system];
						currentAvailableAmount += availableAmount;
					}
				}

				newGroupData[recordIndex] = {
					...groupData[recordIndex],
					...newRecordData,
					currentAvailableAmount,
				};
			}
			return newGroupData;
		}

		for (const tab in newTableData) {
			if (tableData[tab].groups) {
				newTableData[tab].groups = updateGroupData(tableData[tab].groups);
			}
			newTableData[tab].records = replaceTabDataWithInventoryData(tableData[tab].records);
		}

		return newTableData;
	}

	async function fillTableWithCurrentInventory(tableData, lookupData) {
		setLoading(true);
		const { data, error } = await getCurrentInventory();
		if (data) {
			const newTableData = updateTableDataWithInventory(tableData, data.inventoryData, lookupData);
			setInventoryFetched(true);
			setTableData(newTableData);
			successNotification("Success");
		}
		if (error) {
			errorNotification(error);
		}
		setLoading(false);
	}

	return (
		<Page className="no-scrollbar">
			<Header>Create Inventory Plan</Header>
			<Block>
				<Div>Plan Name</Div>
				<InputBox
					defaultValue={planName}
					type="text"
					onChange={(e) => {
						setPlanName(e.target.value);
					}}
				/>

				<Button disabled={inventoryFetched || loading} onClick={() => fillTableWithCurrentInventory(tableData, lookupData)}>
					Fill with Current Inventory
				</Button>

				<Div margin="0 0 0 auto">
					<Button disabled={saveButtonDisabled} onClick={() => saveInventoryPlan()}>
						Save
					</Button>
				</Div>
			</Block>

			<Block>
				<Button active={activeTab === TABS.CONVEYOR_DISPENSERS} onClick={() => setActiveTab(TABS.CONVEYOR_DISPENSERS)}>
					Solid Ingredients
				</Button>
				<Button active={activeTab === TABS.PUMP_DISPENSERS} onClick={() => setActiveTab(TABS.PUMP_DISPENSERS)}>
					Liquid Ingredients
				</Button>
				<Button active={activeTab === TABS.EPRS} onClick={() => setActiveTab(TABS.EPRS)}>
					Bowl Racks
				</Button>
			</Block>

			{renderActiveTab(
				activeTab,
				{ getFilteredOptions, updateTableData, inventoryFetched, erroredItems, removeErroredItem },
				tableData,
				loading
			)}
		</Page>
	);
};

export default LandingConnector(CreateInventoryPlan);
